import { navigate } from '@reach/router';
import Swal from 'sweetalert2';
import { url } from '../../api/api';
import { loadClients } from '../../helpers/loadClients';
import { loadJWT } from '../../helpers/loadJWT';
import { types } from '../types/types';
import { startLogOutContentModal } from './contentModal';
import { finishLoading, startLoading, uiManageModalCollectionTypes } from './ui';

const normalizeNif = nif => (typeof nif === 'string' ? nif.trim() : '');

const findDuplicateClientByNif = async (nif, excludeId) => {
  const normalizedNif = normalizeNif(nif);
  if (!normalizedNif) return null;

  const { token } = loadJWT();
  const whereFilter = {
    NumeroIdentificacionFiscal: normalizedNif,
    FechaBaja: { eq: null },
  };

  if (excludeId) {
    whereFilter.PkClientes = { neq: excludeId };
  }

  const filter = JSON.stringify({
    where: whereFilter,
    limit: 1,
  });

  const response = await url.get(`/clientes?filter=${filter}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response?.data?.length ? response.data[0] : null;
};

export const startNewClient = (client, setSubmitting) => {
  return async dispatch => {
    const { token } = loadJWT();
    try {
      const duplicate = await findDuplicateClientByNif(client?.NumeroIdentificacionFiscal);
      if (duplicate) {
        Swal.fire({
          title: 'Error',
          text: 'Ya existe un cliente con el mismo NIF/CIF/ID.',
          icon: 'error',
          didOpen: () => {
            const swal = document.querySelector('.swal2-container');
            if (swal) swal.style.zIndex = '2000';
          },
        });
        setSubmitting(false);
        return;
      }
    } catch (e) {
      Swal.fire({
        title: 'Error',
        text: e?.response?.data?.error?.message || 'No se ha podido validar el NIF/CIF/ID.',
        icon: 'error',
        didOpen: () => {
          const swal = document.querySelector('.swal2-container');
          if (swal) swal.style.zIndex = '2000';
        },
      });
      setSubmitting(false);
      return;
    }

    await url
      .post('/clientes', client, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        dispatch(startLoadingClients());

        Swal.fire({
          title: 'Éxito',
          text: 'Cliente añadido',
          icon: 'success',
        });

        dispatch(uiManageModalCollectionTypes());
        dispatch(startLogOutContentModal());
      })
      .catch(e => {
        Swal.fire({
          title: 'Error',
          text: e?.response?.data?.error?.message || 'El cliente ya existe',
          icon: 'error',
          didOpen: () => {
            const swal = document.querySelector('.swal2-container');
            if (swal) swal.style.zIndex = '2000';
          },
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
};

export const startUpdateClient = (client, setSubmitting) => {
  return async dispatch => {
    const { token } = loadJWT();
    try {
      const duplicate = await findDuplicateClientByNif(client?.NumeroIdentificacionFiscal, client?.PkClientes);
      if (duplicate) {
        Swal.fire('Error', 'Ya existe un cliente con el mismo NIF/CIF/ID.', 'error');
        setSubmitting(false);
        return;
      }
    } catch (e) {
      Swal.fire('Error', e?.response?.data?.error?.message || 'No se ha podido validar el NIF/CIF/ID.', 'error');
      setSubmitting(false);
      return;
    }
    await url
      .put(`/clientes/${client.PkClientes}`, client, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        Swal.fire('Éxito', 'Cliente modificado con éxito', 'success').then(() => {
          navigate(0);
        });
        return;
      })
      .catch(e => {
        Swal.fire('Error', e?.response?.data?.error?.message || 'No se ha podido editar el cliente', 'error');
        return;
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
};

export const startDeleteCustomer = id => {
  return async dispatch => {
    const { token } = loadJWT();
    await url
      .delete(`/clientes/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        dispatch(deleteClient(id));
        Swal.fire('Éxito', 'Cliente eliminado', 'success');
        return;
      })
      .catch(e => {
        Swal.fire('Error', e?.response?.data?.error?.message || 'Error al eliminar el cliente', 'error');
        return;
      });
  };
};

export const setActiveCustomer = id => {
  const { token } = loadJWT();
  return async dispatch => {
    dispatch(startLoading());
    await url
      .get(`/clientes/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(resp => {
        if (resp.status === 204) {
          navigate('/clients');
          dispatch(finishLoading());
          return;
        }
        dispatch(setActive(resp.data));
        dispatch(finishLoading());
        return;
      })
      .catch(e => {
        Swal.fire('Error', e?.response?.data?.error?.message || 'Error al eliminar el cliente', 'error')
          .then(() => {
            navigate('/clients');
          })
          .then(() => {
            dispatch(finishLoading());
          });
        return;
      });
  };
};

export const setActiveCustomerState = id => {
  const { token } = loadJWT();
  return async dispatch => {
    dispatch(startLoading());
    await url
      .get(`/clientes/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(resp => {
        dispatch(setActive(resp.data));
        return;
      })
      .catch(e => {
        Swal.fire('Error', e?.response?.data?.error?.message || 'Error al eliminar el cliente', 'error');
        return;
      });
  };
};

export const syncNemonClient = (id, setIsLoading) => {
  const { token } = loadJWT();

  return async (dispatch, getState) => {
    setIsLoading(true);
    try {
      const { active } = getState().client;
      const duplicate = await findDuplicateClientByNif(active?.NumeroIdentificacionFiscal, active?.PkClientes);
      if (duplicate) {
        Swal.fire('Error', 'Ya existe un cliente con el mismo NIF/CIF/ID.', 'error');
        setIsLoading(false);
        return;
      }
    } catch (e) {
      Swal.fire('Error', e?.response?.data?.error?.message || 'No se ha podido validar el NIF/CIF/ID.', 'error');
      setIsLoading(false);
      return;
    }
    await url
      .post(
        `/clientes/${id}/nemon-sync`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then(resp => {
        dispatch({ type: types.clientSyncNemon, payload: resp });
        Swal.fire('Éxito', 'Cliente sincronizado con Nemon', 'success').then(() => navigate(0));
      })
      .catch(e => {
        Swal.fire('Error', e?.response?.data?.error?.message || 'Error. Contacte con el administrador.', 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
};

export const validarAEATClient = (id, setIsLoading) => {
  const { token } = loadJWT();
  return async dispatch => {
    setIsLoading(true);

    await url
      .post(
        `/clientes/${id}/validacion-aeat`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then(resp => {
        dispatch({ type: types.clientSyncNemon, payload: resp });
        Swal.fire('Éxito', 'Cliente validado', 'success').then(() => navigate(0));
      })
      .catch(e => {
        Swal.fire('Error', e?.response?.data?.error?.message || 'Error. No se ha podido validar al cliente.', 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
};

export const startLoadingClients = () => {
  return async dispatch => {
    dispatch(startLoading());
    const clients = await loadClients();
    dispatch(setClients(clients));
    dispatch(finishLoading());
  };
};

export const startLoadingAllClients = () => {
  return async dispatch => {
    const { token } = loadJWT();
    dispatch(startLoading());
    await url
      .get('/clientes', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(resp => {
        dispatch(setClients(resp.data));
      })
      .catch(e => {
        Swal.fire('Error', e?.response?.data?.error?.message || 'Error al cargar los clientes', 'error');
      })
      .finally(() => {
        dispatch(finishLoading());
      });
  };
};

const setActive = client => ({
  type: types.clientActive,
  payload: client,
});
export const setClients = clients => ({
  type: types.clientsLoad,
  payload: clients,
});

export const deleteClient = id => ({
  type: types.clientDelete,
  payload: id,
});

const logOut = () => ({
  type: types.clientsLogOutCleaning,
});

export const startLogOutClients = () => {
  return async dispatch => {
    dispatch(logOut());
  };
};
